import * as React from 'react';

import { InviteLearnersModalProvider } from '@coursera-web/enterprise-admin-learners';
import { AIMessageModalProvider } from '@coursera-web/enterprise-admin-messaging-v2';
import { StatsigProvider } from '@coursera-web/statsig';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import ReportsStatusPanel from 'bundles/enterprise-admin-analytics/components/ReportsStatusPanel';
import { ReportsStatusProvider } from 'bundles/enterprise-admin-analytics/components/ReportsStatusProvider';
import EnterpriseAdminPermissionsProvider from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { UserFilterProvider } from 'bundles/enterprise-admin-user-filters/context/userFilterContext';
import { ModalContext, Toaster } from 'bundles/enterprise-ui/components';
import { NotificationsProvider } from 'bundles/enterprise-ui/components/NotificationsContext';
import Naptime from 'bundles/naptimejs';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';

import 'css!./__styles__/AdminDashboardApp';
import 'css!bundles/admin-dashboard/styl/index';

type PropsFromContext = {
  router: InjectedRouter;
};

type PropsFromStore = {
  requestCountryCode: string;
  csrfToken: string;
  children: React.ReactElement;
};

type Props = PropsFromStore & PropsFromContext;

class AdminDashboardApp extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof AdminDashboardApp.contextTypes>;

  static childContextTypes = {
    csrfToken: PropTypes.string,
    requestCountryCode: PropTypes.string,
  };

  getChildContext() {
    const { csrfToken, requestCountryCode } = this.props;
    return {
      csrfToken,
      requestCountryCode,
    };
  }

  UNSAFE_componentWillMount() {
    if (!user.isAuthenticatedUser()) {
      const { router } = this.context;
      const newQueryParams = omit({ ...router.location.query }, 'authMode');
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: newQueryParams,
        hash: buildHashWithAuthMode(getLocationHash(router.location), 'login'),
      });
    }
  }

  render() {
    const { children } = this.props;
    const { router } = this.context;
    const { thirdPartySlug, programSlug } = router.params;
    return (
      <div data-testid="Admin_Dashboard_App" className="rc-AdminDashboardApp">
        <StatsigProvider>
          <NotificationsProvider>
            <Toaster>
              <UserFilterProvider>
                <ReportsStatusProvider>
                  <EnterpriseAdminPermissionsProvider thirdPartySlug={thirdPartySlug} programSlug={programSlug}>
                    <AIMessageModalProvider>
                      <InviteLearnersModalProvider>
                        <ModalContext>{children && React.cloneElement(children)}</ModalContext>
                        <ReportsStatusPanel />
                      </InviteLearnersModalProvider>
                    </AIMessageModalProvider>
                  </EnterpriseAdminPermissionsProvider>
                </ReportsStatusProvider>
              </UserFilterProvider>
            </Toaster>
          </NotificationsProvider>
        </StatsigProvider>
      </div>
    );
  }
}

export default compose<Props, {}>(
  getContext({
    router: PropTypes.object,
  }),
  Naptime.createContainer(() => ({})), // injecting executeMutation in case it's used in ModalContext
  withMarketingConsent,
  connectToStores(['ApplicationStore'], ({ ApplicationStore }, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  })),
  Retracked.createTrackedContainer(({ router }: PropsFromContext) => ({
    namespace: {
      app: 'b2b_admin_dashboard',
      page: 'landingPage',
    },
    // eslint-disable-next-line camelcase
    thirdPartyOrganization_slug: router.params.thirdPartySlug,
  }))
)(AdminDashboardApp);
